import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  CalendarDays,
  ChevronDown,
  FileDown,
  FileSpreadsheet,
  FileText,
  Info,
  Receipt,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import { useCpPortal } from '../../hooks/useCpPortal';

interface NqrLine {
  railId?: string;
  label: string;
  txCount: number;
  nqrCents: number;
}

interface TaxInvoiceRow {
  id: number;
  invoice_serial: string;
  period: string;
  status: 'issued' | 'emailed' | 'email_failed';
  nqr_cents: number;
  revenue_share_pct: string | number;
  consideration_cents: number;
  ex_vat_cents: number;
  vat_cents: number;
  vat_rate: string | number;
  float_credited_cents: number;
  excess_payable_cents: number;
  accrual_journal_reference?: string | null;
  settlement_journal_reference?: string | null;
  line_detail?: NqrLine[];
  emailed_at: string | null;
  created_at: string;
}

interface FinanceEstimate {
  period: string;
  nqrCents?: number;
  revenueSharePct?: number | null;
  considerationCents?: number;
  exVatCents?: number;
  vatCents?: number;
  lines?: NqrLine[];
  unavailable?: boolean;
}

interface FinanceOverview {
  revenueShareEnabled: boolean;
  revenueSharePct: number | null;
  isVatVendor: boolean;
  float: { floatBalanceCents: number; reservedCents: number; availableCents: number } | null;
  latestInvoice: TaxInvoiceRow | null;
  estimate: FinanceEstimate | null;
  nextSettlementDate: string;
}

interface StatementMovement {
  date: string;
  typeId: string;
  label: string;
  reference: string | null;
  debitCents: number;
  creditCents: number;
  balanceAfterCents: number;
  entryCount: number;
}

interface StatementData {
  accountCode: string;
  period: { from: string; to: string };
  openingCents: number;
  closingCents: number;
  movements: StatementMovement[];
  summary: {
    debitCents: number;
    creditCents: number;
    byType: Record<string, { debitCents: number; creditCents: number; entryCount: number }>;
  };
}

type Preset = '30d' | '90d' | 'month' | 'lastMonth' | 'custom';
type ExportFormat = 'csv' | 'xlsx' | 'pdf';

const MAX_RANGE_DAYS = 366;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function presetRange(preset: Exclude<Preset, 'custom'>): { from: string; to: string } {
  const today = new Date();
  const to = toIsoDate(today);
  if (preset === 'month') {
    return { from: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to };
  }
  if (preset === 'lastMonth') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: toIsoDate(first), to: toIsoDate(last) };
  }
  const days = preset === '30d' ? 29 : 89;
  return { from: toIsoDate(new Date(today.getTime() - days * 86400000)), to };
}

function matchPreset(from: string, to: string): Preset {
  const presets: Exclude<Preset, 'custom'>[] = ['lastMonth', 'month', '30d', '90d'];
  for (const p of presets) {
    const r = presetRange(p);
    if (r.from === from && r.to === to) return p;
  }
  return 'custom';
}

function rangeDays(from: string, to: string): number {
  return Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000) + 1;
}

const formatRand = (cents: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(cents / 100);

function formatPeriod(period: string): string {
  const d = new Date(`${period}-01T00:00:00+02:00`);
  return d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'Africa/Johannesburg' });
}

function formatDay(day: string): string {
  return new Date(`${day}T00:00:00+02:00`).toLocaleDateString('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg',
  });
}

export const RedirectStatementsToFinance: React.FC = () => {
  const { cpPath } = useCpPortal();
  const { search } = useLocation();
  return <Navigate to={`${cpPath('/finance')}${search}`} replace />;
};

export const ClientFinanceOverlay: React.FC = () => {
  const { apiBase, authHeaders, user, isMirror } = useCpPortal();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = isMirror || user?.role === 'admin';

  const urlFrom = ISO_DATE.test(searchParams.get('from') || '') ? searchParams.get('from')! : null;
  const urlTo = ISO_DATE.test(searchParams.get('to') || '') ? searchParams.get('to')! : null;
  const initialRange = urlFrom && urlTo && urlFrom <= urlTo
    ? { from: urlFrom, to: urlTo }
    : presetRange('lastMonth');

  const [preset, setPreset] = useState<Preset>(() => matchPreset(initialRange.from, initialRange.to));
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);

  const [overview, setOverview] = useState<FinanceOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [invoices, setInvoices] = useState<TaxInvoiceRow[] | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const [statement, setStatement] = useState<StatementData | null>(null);
  const [statementLoading, setStatementLoading] = useState(true);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);

  const [error, setError] = useState<string | null>(null);

  const syncUrl = useCallback((nextFrom: string, nextTo: string) => {
    setSearchParams({ from: nextFrom, to: nextTo }, { replace: true });
  }, [setSearchParams]);

  const downloadBlob = useCallback(async (url: string, fallbackName: string) => {
    const res = await fetch(url, { headers: authHeaders() });
    if (res.status === 403) {
      throw new Error('You do not have permission to download this file');
    }
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Download failed');
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = match?.[1] || fallbackName;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [authHeaders]);

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/reports/finance-overview`, { headers: authHeaders() });
      if (res.status === 403) {
        setError('Finance is available to channel partner administrators only');
        setOverview(null);
        return;
      }
      const data = await res.json();
      if (data.success) setOverview(data.data as FinanceOverview);
      else setError(data.error || 'Failed to load finance overview');
    } catch {
      setError('Network error loading finance overview');
    } finally {
      setOverviewLoading(false);
    }
  }, [apiBase, authHeaders]);

  const fetchInvoices = useCallback(async () => {
    if (!overview) return;
    if (!overview.revenueShareEnabled) {
      setInvoices([]);
      setInvoicesLoading(false);
      return;
    }
    setInvoicesLoading(true);
    try {
      const res = await fetch(`${apiBase}/reports/invoices`, { headers: authHeaders() });
      const data = await res.json();
      if (res.status === 403) {
        setInvoices([]);
        return;
      }
      if (data.success) setInvoices(data.data as TaxInvoiceRow[]);
      else setError(data.error || 'Failed to load invoices');
    } catch {
      setError('Network error loading invoices');
    } finally {
      setInvoicesLoading(false);
    }
  }, [apiBase, authHeaders, overview]);

  const fetchStatement = useCallback(async () => {
    if (!from || !to) return;
    setStatementLoading(true);
    try {
      const res = await fetch(`${apiBase}/reports/statement?from=${from}&to=${to}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.status === 403) {
        setError('Finance is available to channel partner administrators only');
        setStatement(null);
        return;
      }
      if (data.success) setStatement(data.data as StatementData);
      else {
        setError(data.error || 'Failed to load statement');
        setStatement(null);
      }
    } catch {
      setError('Network error loading statement');
      setStatement(null);
    } finally {
      setStatementLoading(false);
    }
  }, [apiBase, authHeaders, from, to]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchStatement(); }, [fetchStatement]);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'custom') return;
    const range = presetRange(p);
    setFrom(range.from);
    setTo(range.to);
    syncUrl(range.from, range.to);
  };

  const handleInvoiceDownload = async (invoice: TaxInvoiceRow, format: ExportFormat) => {
    const key = `${invoice.id}:${format}`;
    setDownloading(key);
    setError(null);
    try {
      await downloadBlob(
        `${apiBase}/reports/invoices/${invoice.id}/download?format=${format}`,
        `${invoice.invoice_serial}.${format}`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const handleStatementExport = async (format: ExportFormat) => {
    setExportingFormat(format);
    setError(null);
    try {
      await downloadBlob(
        `${apiBase}/reports/statement/export?from=${from}&to=${to}&format=${format}`,
        `statement.${format}`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setExportingFormat(null);
    }
  };

  const customRangeInvalid = preset === 'custom' && (!from || !to || from > to || rangeDays(from, to) > MAX_RANGE_DAYS);

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl">
        <div className="flex items-start gap-3 rounded-xl border border-[var(--border)] bg-[var(--card)] p-6">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--muted-foreground)]" aria-hidden />
          <div>
            <h2 className="text-sm font-semibold text-[var(--foreground)]">Administrators only</h2>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Finance documents are available to channel partner administrators. Ask an admin on your team if you need access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const pctLabel = overview?.revenueSharePct != null
    ? `${Number(overview.revenueSharePct).toFixed(2)}%`
    : null;
  const latest = overview?.latestInvoice;
  const estimate = overview?.estimate;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-start gap-2.5">
        <Receipt className="mt-0.5 h-5 w-5 text-[var(--primary)]" aria-hidden />
        <div>
          <h2 className="text-sm font-semibold text-[var(--foreground)]">Finance</h2>
          <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">
            Prefunded float statement for your organisation.
            {overview?.revenueShareEnabled
              ? ' Revenue share is a percentage of Net Qualifying Revenue (NQR) — MyMoolah’s own earned revenue — not of purchase face value. Transaction mix under Reports is activity, not the revenue-share base.'
              : ' Revenue-share invoices appear here only when a revenue-share agreement is enabled.'}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700" role="alert">
          <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <section className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)]">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <h3 className="text-sm font-semibold text-[var(--foreground)]">Overview</h3>
          <button
            type="button"
            onClick={fetchOverview}
            disabled={overviewLoading}
            className="flex min-h-[32px] items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-1 text-xs text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${overviewLoading ? 'animate-spin' : ''}`} aria-hidden />
            Refresh
          </button>
        </div>
        {overviewLoading ? (
          <div className="grid grid-cols-1 gap-0 divide-y divide-[var(--border)] sm:grid-cols-3 sm:divide-x sm:divide-y-0" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 animate-pulse bg-[var(--muted)]/40" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 divide-y divide-[var(--border)] sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <div className="px-4 py-4">
              <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Available float</p>
              <p className="mt-1 text-lg font-bold tabular-nums text-[var(--foreground)]">
                {overview?.float ? formatRand(overview.float.availableCents) : '—'}
              </p>
              {overview?.float && overview.float.reservedCents > 0 && (
                <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                  {formatRand(overview.float.reservedCents)} reserved
                </p>
              )}
            </div>
            <div className="px-4 py-4">
              {overview?.revenueShareEnabled ? (
                <>
                  <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                    {estimate?.period ? `${formatPeriod(estimate.period)} estimate` : 'Current-month estimate'}
                  </p>
                  {estimate?.unavailable ? (
                    <p className="mt-1 text-sm text-[var(--muted-foreground)]">Estimate unavailable</p>
                  ) : (
                    <>
                      <p className="mt-1 text-lg font-bold tabular-nums text-[var(--foreground)]">
                        {formatRand(estimate?.considerationCents || 0)}
                      </p>
                      <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                        {pctLabel} of NQR {formatRand(estimate?.nqrCents || 0)} — not yet credited.
                        Scheduled float credit {overview.nextSettlementDate ? formatDay(overview.nextSettlementDate) : '1st of next month'}, 02:00 SAST.
                      </p>
                    </>
                  )}
                </>
              ) : (
                <>
                  <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Revenue share</p>
                  <p className="mt-1 text-sm font-semibold text-[var(--foreground)]">Not enabled for this partner</p>
                  <p className="mt-1 text-xs text-[var(--muted-foreground)]">Float statements remain available.</p>
                </>
              )}
            </div>
            <div className="px-4 py-4">
              <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Last settlement</p>
              {latest && latest.float_credited_cents > 0 ? (
                <>
                  <p className="mt-1 text-lg font-bold tabular-nums text-[var(--foreground)]">
                    {formatRand(latest.float_credited_cents)}
                  </p>
                  <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                    {formatPeriod(latest.period)} NQR share credited to float
                    {latest.created_at ? ` on ${formatDay(latest.created_at.slice(0, 10))}` : ''}.
                    {latest.settlement_journal_reference ? ` ${latest.settlement_journal_reference}.` : ''}
                  </p>
                </>
              ) : latest ? (
                <>
                  <p className="mt-1 text-sm font-semibold text-[var(--foreground)]">{latest.invoice_serial}</p>
                  <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                    Issued for {formatPeriod(latest.period)} — not credited to float.
                  </p>
                </>
              ) : (
                <p className="mt-1 text-sm text-[var(--muted-foreground)]">No settlement yet</p>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)]">
        <div className="border-b border-[var(--border)] px-4 py-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[var(--muted-foreground)]" aria-hidden />
              <h3 className="text-sm font-semibold text-[var(--foreground)]">Float statement</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={fetchStatement}
                disabled={statementLoading}
                className="flex min-h-[32px] items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-1 text-xs text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${statementLoading ? 'animate-spin' : ''}`} aria-hidden />
                Refresh
              </button>
              {(['csv', 'xlsx', 'pdf'] as ExportFormat[]).map((format) => {
                const Icon = format === 'csv' ? FileText : format === 'xlsx' ? FileSpreadsheet : FileDown;
                return (
                  <button
                    key={format}
                    type="button"
                    onClick={() => handleStatementExport(format)}
                    disabled={statementLoading || exportingFormat !== null || !statement}
                    className="flex min-h-[32px] items-center gap-1 rounded-lg border border-[var(--border)] px-2.5 py-1 text-[11px] uppercase text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] disabled:opacity-50"
                  >
                    {exportingFormat === format ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                    )}
                    {format}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-1.5">
              {([
                ['30d', 'Last 30 days'],
                ['90d', 'Last 90 days'],
                ['month', 'This month'],
                ['lastMonth', 'Last month'],
                ['custom', 'Custom'],
              ] as [Preset, string][]).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => applyPreset(value)}
                  className={`min-h-[32px] rounded-lg border px-3 py-1 text-xs transition-colors ${
                    preset === value
                      ? 'border-[var(--primary)] bg-[var(--primary)]/10 font-medium text-[var(--primary)]'
                      : 'border-[var(--border)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {preset === 'custom' && (
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label htmlFor="stmt-from" className="mb-1 block text-xs font-medium text-[var(--foreground)]">From</label>
                  <input
                    id="stmt-from"
                    type="date"
                    value={from}
                    max={to}
                    onChange={(e) => {
                      setFrom(e.target.value);
                      if (e.target.value && to && e.target.value <= to) syncUrl(e.target.value, to);
                    }}
                    className="min-h-[32px] rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-1 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)]"
                  />
                </div>
                <div>
                  <label htmlFor="stmt-to" className="mb-1 block text-xs font-medium text-[var(--foreground)]">To</label>
                  <input
                    id="stmt-to"
                    type="date"
                    value={to}
                    min={from}
                    max={toIsoDate(new Date())}
                    onChange={(e) => {
                      setTo(e.target.value);
                      if (from && e.target.value && from <= e.target.value) syncUrl(from, e.target.value);
                    }}
                    className="min-h-[32px] rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-1 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)]"
                  />
                </div>
                {customRangeInvalid && (
                  <p className="text-xs text-red-600">Enter a valid range of up to {MAX_RANGE_DAYS} days.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 divide-x divide-[var(--border)] border-b border-[var(--border)]">
          <div className="px-4 py-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Opening</p>
            <p className="mt-1 text-sm font-bold tabular-nums text-[var(--foreground)]">
              {statement ? formatRand(statement.openingCents) : '—'}
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Net movement</p>
            <p className="mt-1 text-sm font-bold tabular-nums text-[var(--foreground)]">
              {statement ? formatRand(statement.summary.creditCents - statement.summary.debitCents) : '—'}
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Closing</p>
            <p className="mt-1 text-sm font-bold tabular-nums text-[var(--foreground)]">
              {statement ? formatRand(statement.closingCents) : '—'}
            </p>
          </div>
        </div>

        {statementLoading ? (
          <div className="space-y-2 p-4" aria-busy="true">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-[var(--muted)]/60" />
            ))}
          </div>
        ) : !statement || statement.movements.length === 0 ? (
          <div className="p-10 text-center">
            <CalendarDays className="mx-auto mb-3 h-8 w-8 text-[var(--muted-foreground)]" aria-hidden />
            <p className="text-sm font-medium text-[var(--foreground)]">No movements in this period</p>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">Try a wider date range.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]/40 text-left">
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Date</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Movement</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Out</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">In</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Balance</th>
                </tr>
              </thead>
              <tbody>
                {statement.movements.map((m, i) => (
                  <tr key={`${m.date}-${i}`} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--muted)]/30">
                    <td className="px-4 py-2.5 whitespace-nowrap text-[var(--muted-foreground)]">{formatDay(m.date)}</td>
                    <td className="px-4 py-2.5 font-medium text-[var(--foreground)]">
                      {m.label}
                      {m.entryCount > 1 && (
                        <span className="ml-1.5 text-xs text-[var(--muted-foreground)]">({m.entryCount} entries)</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[var(--muted-foreground)]">
                      {m.debitCents > 0 ? formatRand(m.debitCents) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[var(--muted-foreground)]">
                      {m.creditCents > 0 ? formatRand(m.creditCents) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-[var(--foreground)]">
                      {formatRand(m.balanceAfterCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)]">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-[var(--muted-foreground)]" aria-hidden />
            <h3 className="text-sm font-semibold text-[var(--foreground)]">Tax invoices</h3>
          </div>
          {overview?.revenueShareEnabled && (
            <button
              type="button"
              onClick={fetchInvoices}
              disabled={invoicesLoading}
              className="flex min-h-[32px] items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-1 text-xs text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${invoicesLoading ? 'animate-spin' : ''}`} aria-hidden />
              Refresh
            </button>
          )}
        </div>

        {overviewLoading ? (
          <div className="space-y-2 p-4" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-[var(--muted)]/60" />
            ))}
          </div>
        ) : !overview?.revenueShareEnabled ? (
          <div className="flex items-start gap-3 p-6">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--muted-foreground)]" aria-hidden />
            <div>
              <p className="text-sm font-semibold text-[var(--foreground)]">Revenue share not enabled for this partner</p>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                Recipient-created tax invoices are issued only under a revenue-share agreement. Float statements above remain available.
              </p>
            </div>
          </div>
        ) : invoicesLoading ? (
          <div className="space-y-2 p-4" aria-busy="true">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-[var(--muted)]/60" />
            ))}
          </div>
        ) : !invoices || invoices.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm font-medium text-[var(--foreground)]">No invoices yet</p>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Monthly invoices are issued on the 1st at 02:00 SAST for the prior month’s NQR share.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]/40 text-left">
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Invoice</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Period</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">NQR</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Share</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Credited</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Download</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const open = expandedInvoiceId === inv.id;
                  const lines = Array.isArray(inv.line_detail) ? inv.line_detail : [];
                  return (
                    <React.Fragment key={inv.id}>
                      <tr className="border-b border-[var(--border)] hover:bg-[var(--muted)]/30">
                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            onClick={() => setExpandedInvoiceId(open ? null : inv.id)}
                            aria-expanded={open}
                            className="flex items-center gap-1.5 text-left"
                          >
                            <ChevronDown className={`h-3.5 w-3.5 text-[var(--muted-foreground)] transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
                            <span className="font-mono text-xs font-medium text-[var(--foreground)]">{inv.invoice_serial}</span>
                          </button>
                        </td>
                        <td className="px-4 py-2.5 text-[var(--foreground)]">{formatPeriod(inv.period)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-[var(--muted-foreground)]">{formatRand(inv.nqr_cents)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-medium text-[var(--foreground)]">
                          {formatRand(inv.consideration_cents)}
                          <span className="ml-1 text-[10px] font-normal text-[var(--muted-foreground)]">
                            {Number(inv.revenue_share_pct).toFixed(0)}% NQR
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-[var(--muted-foreground)]">
                          {inv.float_credited_cents > 0 ? formatRand(inv.float_credited_cents) : '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex justify-end gap-1">
                            {(['pdf', 'csv', 'xlsx'] as ExportFormat[]).map((format) => {
                              const Icon = format === 'csv' ? FileText : format === 'xlsx' ? FileSpreadsheet : FileDown;
                              const key = `${inv.id}:${format}`;
                              return (
                                <button
                                  key={format}
                                  type="button"
                                  onClick={() => handleInvoiceDownload(inv, format)}
                                  disabled={downloading !== null}
                                  title={`Download ${format.toUpperCase()}`}
                                  className="flex min-h-[32px] items-center gap-1 rounded-lg border border-[var(--border)] px-2 py-1 text-[11px] uppercase text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] disabled:opacity-50"
                                >
                                  {downloading === key ? (
                                    <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                  ) : (
                                    <Icon className="h-3.5 w-3.5" aria-hidden />
                                  )}
                                  {format}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr className="border-b border-[var(--border)] bg-[var(--muted)]/20">
                          <td colSpan={6} className="px-6 py-3">
                            <p className="text-xs text-[var(--muted-foreground)]">
                              {Number(inv.revenue_share_pct).toFixed(2)}% of NQR {formatRand(inv.nqr_cents)}
                              {' = '}{formatRand(inv.consideration_cents)}
                              {Number(inv.vat_cents) > 0 ? ` (ex-VAT ${formatRand(inv.ex_vat_cents)} + VAT ${formatRand(inv.vat_cents)})` : ' (not a VAT vendor — 0% VAT)'}.
                              {inv.excess_payable_cents > 0 ? ` Excess payable ${formatRand(inv.excess_payable_cents)}.` : ''}
                              {inv.settlement_journal_reference ? ` Settlement ${inv.settlement_journal_reference}.` : ' Document issued is not proof of float credit.'}
                            </p>
                            {lines.length > 0 && (
                              <table className="mt-3 w-full max-w-xl text-xs">
                                <thead>
                                  <tr className="text-left text-[var(--muted-foreground)]">
                                    <th className="py-1 font-medium">NQR line</th>
                                    <th className="py-1 text-right font-medium">Tx</th>
                                    <th className="py-1 text-right font-medium">NQR</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {lines.map((line) => (
                                    <tr key={`${inv.id}-${line.railId || line.label}`}>
                                      <td className="py-1 text-[var(--foreground)]">{line.label}</td>
                                      <td className="py-1 text-right tabular-nums">{line.txCount}</td>
                                      <td className="py-1 text-right tabular-nums">{formatRand(line.nqrCents)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="flex items-start gap-2 rounded-xl border border-[var(--border)] bg-[var(--muted)]/30 px-4 py-3">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--muted-foreground)]" aria-hidden />
        <p className="text-xs text-[var(--muted-foreground)]">
          {overview?.revenueShareEnabled
            ? `Invoices are recipient-created tax invoices issued by MyMoolah on behalf of${user?.companyName ? ` ${user.companyName}` : ' your organisation'} under the self-billing authorisation in the Channel Partner Revenue Share Agreement. The share is a percentage of Net Qualifying Revenue, not of customer spend.`
            : 'Statements reflect the prefunded float ledger account only.'}
          {' '}No individual customer data is included. Reports (transaction mix) is a separate activity view — it is not the NQR base.
        </p>
      </div>
    </div>
  );
};
